<template>
  <div class="mall-ship" :class="{ 'is-embedded': isEmbedded }">
    <div class="mall-ship__shell">
      <header class="mall-ship__head">
        <div>
          <h1>배송지 관리</h1>
          <p>주문/결제 시 바로 쓸 배송지와 기본 요청사항을 관리합니다.</p>
        </div>
        <button type="button" class="mall-ship__primary" @click="startCreate">
          새 배송지
        </button>
      </header>

      <p v-if="errorMessage" class="mall-ship__error">
        <AppIcon icon="mdi-alert-outline" :size="16" />
        {{ errorMessage }}
      </p>
      <p v-if="toastMessage" class="mall-ship__toast">{{ toastMessage }}</p>

      <section class="mall-ship__card">
        <div class="mall-ship__card-head">
          <h2>저장된 배송지</h2>
          <span>{{ addresses.length }} / 10</span>
        </div>

        <div v-if="loading" class="mall-ship__empty">
          <p>불러오는 중입니다…</p>
        </div>

        <div v-else-if="!addresses.length" class="mall-ship__empty">
          <AppIcon icon="mdi-map-marker-off-outline" :size="40" />
          <p>저장된 배송지가 없습니다. 자주 쓰는 주소를 등록해 두세요.</p>
        </div>

        <ul v-else class="mall-ship__list">
          <li v-for="item in addresses" :key="item.idx" class="mall-ship__item">
            <div class="mall-ship__item-body">
              <div class="mall-ship__item-top">
                <strong>{{ item.label }}</strong>
                <em v-if="item.isDefault">기본</em>
              </div>
              <p>{{ item.receiver }} · {{ item.phone }}</p>
              <p>
                <template v-if="item.zipcode">[{{ item.zipcode }}] </template>
                {{ item.address }}
                <template v-if="item.addressDetail"> {{ item.addressDetail }}</template>
              </p>
            </div>
            <div class="mall-ship__item-actions">
              <button
                v-if="!item.isDefault"
                type="button"
                class="mall-ship__ghost"
                :disabled="busy"
                @click="onSetDefault(item)"
              >
                기본으로
              </button>
              <button type="button" class="mall-ship__ghost" :disabled="busy" @click="startEdit(item)">
                수정
              </button>
              <button type="button" class="mall-ship__ghost is-danger" :disabled="busy" @click="onDelete(item)">
                삭제
              </button>
            </div>
          </li>
        </ul>
      </section>

      <section v-if="editorOpen" class="mall-ship__card">
        <div class="mall-ship__card-head">
          <h2>{{ editingIdx ? '배송지 수정' : '새 배송지' }}</h2>
          <button type="button" class="mall-ship__text" @click="closeEditor">닫기</button>
        </div>

        <form class="mall-ship__form" @submit.prevent="onSaveAddress">
          <label>
            <span>배송지 이름</span>
            <input v-model.trim="form.label" maxlength="40" placeholder="예: 본원 · 분원 · 물류창고" />
          </label>
          <div class="mall-ship__row">
            <label>
              <span>수령인</span>
              <input v-model.trim="form.receiver" required maxlength="50" placeholder="이름" />
            </label>
            <label>
              <span>연락처</span>
              <input v-model.trim="form.phone" required maxlength="30" placeholder="010-0000-0000" />
            </label>
          </div>
          <label>
            <span>주소</span>
            <div class="mall-ship__zip">
              <input :value="form.zipcode" type="text" placeholder="우편번호" readonly @click="onSearchAddress" />
              <button type="button" class="mall-ship__ghost" :disabled="postcodeOpening" @click="onSearchAddress">
                주소 검색
              </button>
            </div>
            <input
              :value="form.address"
              type="text"
              required
              placeholder="주소 검색을 눌러 주세요"
              readonly
              @click="onSearchAddress"
            />
            <input
              v-model.trim="form.addressDetail"
              type="text"
              maxlength="80"
              placeholder="상세 주소 (동·호수 등)"
            />
          </label>
          <label class="mall-ship__check">
            <input v-model="form.isDefault" type="checkbox" />
            <span>기본 배송지로 사용</span>
          </label>
          <div class="mall-ship__form-actions">
            <button type="button" class="mall-ship__ghost" @click="closeEditor">취소</button>
            <button type="submit" class="mall-ship__primary" :disabled="busy">
              {{ busy ? '저장 중…' : '배송지 저장' }}
            </button>
          </div>
        </form>
      </section>

      <section class="mall-ship__card">
        <div class="mall-ship__card-head">
          <h2>기본 요청사항</h2>
        </div>
        <form class="mall-ship__form" @submit.prevent="onSaveMemo">
          <div class="mall-ship__presets">
            <button
              v-for="preset in MEMO_PRESETS"
              :key="preset"
              type="button"
              class="mall-ship__chip"
              :class="{ 'is-active': memo === preset }"
              @click="memo = preset"
            >
              {{ preset }}
            </button>
          </div>
          <label>
            <span>요청사항</span>
            <input v-model.trim="memo" maxlength="255" placeholder="배송 시 요청사항을 입력해 주세요" />
          </label>
          <div class="mall-ship__form-actions">
            <button type="submit" class="mall-ship__primary" :disabled="busy">
              {{ busy ? '저장 중…' : '요청사항 저장' }}
            </button>
          </div>
        </form>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import AppIcon from '@/components/common/AppIcon.vue'
import { useDaumPostcode } from '@/composables/useDaumPostcode'
import { useMallMypageEmbedded } from '@/composables/useMallMypage'
import { useMemberAuth } from '@/composables/useMemberAuth'
import {
  createShippingAddress,
  deleteShippingAddress,
  fetchShippingBook,
  saveShippingMemo,
  updateShippingAddress,
  type MallShippingAddress,
} from '@/api/mallShipping'

const MEMO_PRESETS = [
  '배송 전 연락 바랍니다',
  '문 앞에 놓아주세요',
  '경비실에 맡겨주세요',
  '직접 받겠습니다',
] as const

const { member } = useMemberAuth()
const { opening: postcodeOpening, searchPostcode } = useDaumPostcode()
const isEmbedded = useMallMypageEmbedded()

const addresses = ref<MallShippingAddress[]>([])
const memo = ref('')
const loading = ref(true)
const busy = ref(false)
const editorOpen = ref(false)
const editingIdx = ref<number | null>(null)
const errorMessage = ref('')
const toastMessage = ref('')

const form = reactive({
  label: '',
  receiver: '',
  phone: '',
  zipcode: '',
  address: '',
  addressDetail: '',
  isDefault: true,
})

function showToast(message: string) {
  toastMessage.value = message
  window.setTimeout(() => {
    if (toastMessage.value === message) toastMessage.value = ''
  }, 2200)
}

async function reload() {
  loading.value = true
  errorMessage.value = ''
  try {
    const book = await fetchShippingBook()
    addresses.value = book.addresses
    memo.value = book.pref.lastMemo || ''
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '배송지를 불러오지 못했습니다.'
  } finally {
    loading.value = false
  }
}

function startCreate() {
  editingIdx.value = null
  form.label = ''
  form.receiver = member.value?.bossName || member.value?.orgName || ''
  form.phone = member.value?.bossPhone || member.value?.orgPhone || ''
  form.zipcode = ''
  form.address = ''
  form.addressDetail = ''
  form.isDefault = addresses.value.length === 0
  editorOpen.value = true
}

function startEdit(item: MallShippingAddress) {
  editingIdx.value = item.idx
  form.label = item.label
  form.receiver = item.receiver
  form.phone = item.phone
  form.zipcode = item.zipcode
  form.address = item.address
  form.addressDetail = item.addressDetail
  form.isDefault = item.isDefault
  editorOpen.value = true
}

function closeEditor() {
  editorOpen.value = false
  editingIdx.value = null
}

async function onSearchAddress() {
  try {
    const picked = await searchPostcode()
    if (!picked) return
    form.zipcode = picked.zipcode
    form.address = picked.extra ? `${picked.address} ${picked.extra}` : picked.address
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '주소 검색에 실패했습니다.'
  }
}

async function onSaveAddress() {
  if (busy.value) return
  busy.value = true
  errorMessage.value = ''
  try {
    const payload = {
      label: form.label || '기본 배송지',
      receiver: form.receiver,
      phone: form.phone,
      zipcode: form.zipcode,
      address: form.address,
      addressDetail: form.addressDetail,
      isDefault: form.isDefault,
    }
    if (editingIdx.value) {
      await updateShippingAddress(editingIdx.value, payload)
      showToast('배송지를 수정했습니다.')
    } else {
      await createShippingAddress(payload)
      showToast('배송지를 저장했습니다.')
    }
    closeEditor()
    await reload()
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '배송지 저장에 실패했습니다.'
  } finally {
    busy.value = false
  }
}

async function onSetDefault(item: MallShippingAddress) {
  if (busy.value) return
  busy.value = true
  errorMessage.value = ''
  try {
    await updateShippingAddress(item.idx, {
      label: item.label,
      receiver: item.receiver,
      phone: item.phone,
      zipcode: item.zipcode,
      address: item.address,
      addressDetail: item.addressDetail,
      isDefault: true,
    })
    showToast('기본 배송지로 설정했습니다.')
    await reload()
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '기본 배송지 설정에 실패했습니다.'
  } finally {
    busy.value = false
  }
}

async function onDelete(item: MallShippingAddress) {
  if (busy.value) return
  if (!window.confirm(`「${item.label}」 배송지를 삭제할까요?`)) return
  busy.value = true
  errorMessage.value = ''
  try {
    await deleteShippingAddress(item.idx)
    if (editingIdx.value === item.idx) closeEditor()
    showToast('배송지를 삭제했습니다.')
    await reload()
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '배송지를 삭제하지 못했습니다.'
  } finally {
    busy.value = false
  }
}

async function onSaveMemo() {
  if (busy.value) return
  busy.value = true
  errorMessage.value = ''
  try {
    const pref = await saveShippingMemo(memo.value)
    memo.value = pref.lastMemo || ''
    showToast('요청사항을 저장했습니다.')
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '요청사항 저장에 실패했습니다.'
  } finally {
    busy.value = false
  }
}

onMounted(reload)
</script>

<style lang="scss" src="./MallShippingPage.scss"></style>
